import type React from 'react'

import { PiWarningBold } from 'react-icons/pi'

import { useDashboard, useCompany } from '@/hooks'
import { AssetInsightHistory } from '@/insights/assets/assetHistory/AssetInsightHistory'
import { AssetRouteHistory } from '@/insights/assets/routeHistoryMap/AssetRouteHistory'
import { useAssetWindow } from '@/insights/common/assetHistoryWindow'
import { getUserTimezone } from '@/insights/common/timezone'
import type { AssetState, Severity } from '@/types/asset-types'

import AlertsAlarmsPanel from './AlertsAlarmsPanel'
import AssetGraphs from './AssetGraphs'
import AssetOverview from './AssetOverview'

const SEVERITY_RANK: Record<Severity, number> = { critical: 3, warning: 2, informational: 1 }

// Matches the pill text colors in ActiveAlertsCard, so the tab icon reads as the same severity.
const SEVERITY_TEXT_COLOR: Record<Severity, string> = {
  critical: 'text-[#C51616]',
  warning: 'text-[#A37B00]',
  informational: 'text-[#2A693A]',
}

export type AssetDetailTab = 'overview' | 'alerts' | 'graph' | 'history' | 'route'

export interface AssetDetailContentProps {
  asset: AssetState
  companyId: number | undefined
  activeTab: AssetDetailTab
  setActiveTab: (tab: AssetDetailTab) => void
}

/**
 * The tabs and their bodies, shared by the asset detail modal (insight reports) and the
 * asset detail sheet (asset map), so the two always show the same thing. Each wrapper
 * remounts this per asset, which resets the period to today.
 *
 * The Graphs, History and Route Map tabs share one period: moving the dates in one moves
 * them in the others. Only one tab is mounted at a time, so each graph opens on the shared
 * period and reports back whenever it moves.
 */
export default function AssetDetailContent({ asset, companyId, activeTab, setActiveTab }: AssetDetailContentProps) {
  const { companies } = useCompany()
  const company = companies.find((item) => item.id === companyId)

  const assetWindow = useAssetWindow(getUserTimezone())

  const units = useDashboard().configuration.units as 'Imperial' | 'Metric'
  const hasAlertsOrAlarms = asset.activeAlarms.length + asset.activeAlerts.length > 0
  const highestSeverity = [...asset.activeAlarms, ...asset.activeAlerts].reduce<Severity | null>(
    (highest, { severity }) => (!highest || SEVERITY_RANK[severity] > SEVERITY_RANK[highest] ? severity : highest),
    null
  )

  const tabs: { id: AssetDetailTab; label: React.ReactNode }[] = [
    { id: 'overview', label: 'Overview' },
    {
      id: 'alerts',
      label: (
        <span className="flex items-center gap-1">
          Alerts &amp; Alarms
          {hasAlertsOrAlarms && (
            <PiWarningBold
              className={`h-4 w-4 ${highestSeverity ? SEVERITY_TEXT_COLOR[highestSeverity] : 'text-warning'}`}
              aria-label="Has active alerts"
            />
          )}
        </span>
      ),
    },
    { id: 'graph', label: 'Graphs' },
    // MEGA-828: History and Route Map are open to everyone (they were support-only).
    ...(companyId != null
      ? [
          { id: 'history' as const, label: 'History' },
          { id: 'route' as const, label: 'Route Map' },
        ]
      : []),
  ]

  return (
    // Fills the sheet's height, so the route map can take whatever the tabs leave.
    <div className="@container flex h-full flex-col">
      <div role="tablist" className="tabs tabs-border mb-2">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={activeTab === tab.id}
            className={`tab ${activeTab === tab.id ? 'tab-active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="flex min-h-[450px] flex-1 flex-col">
        {activeTab === 'overview' && (
          <AssetOverview asset={asset} company={company} companyId={companyId} units={units} />
        )}
        {activeTab === 'alerts' && <AlertsAlarmsPanel asset={asset} />}
        {activeTab === 'graph' && (
          <AssetGraphs assetId={asset.id} companyId={companyId} units={units} assetWindow={assetWindow} />
        )}
        {activeTab === 'history' && companyId != null && (
          <div className="relative min-h-[450px] flex-1">
            <div className="absolute inset-0">
              <AssetInsightHistory
                companyId={companyId}
                assetId={asset.id}
                asset={asset}
                company={company}
                assetWindow={assetWindow}
              />
            </div>
          </div>
        )}
        {activeTab === 'route' && companyId != null && (
          // Down to the bottom of the sheet; at least 450px, which is its height in the modal.
          <div className="relative min-h-[450px] flex-1">
            <div className="absolute inset-0">
              <AssetRouteHistory companyId={companyId} assetId={asset.id} assetWindow={assetWindow} />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
